import { useState, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { debounce } from "lodash"
import { getQuestionnaireTemplates } from "store/question"
import { DEFAULT_PAGE_SIZE, INITIAL_PAGE, DEBOUNCE_DELAY } from "../constants"

export const useTemplates = (tab, searchText) => {
    const dispatch = useDispatch()
    const [pagination, setPagination] = useState({
        current: INITIAL_PAGE,
        pageSize: DEFAULT_PAGE_SIZE,
        total: 0
    })

    // Select data from Redux store
    const questionState = useSelector((state) => state.question.questionnaireTemplates || {})
    const loading = useSelector((state) => state.question.loading)
    const metadata = useSelector((state) => state.question.metadata || {})

    // Extract data from the response
    const templates = Array.isArray(questionState.data) ? questionState.data : []

    // Memoized debounced search function
    const debouncedSearch = useMemo(
        () =>
            debounce((term, currentTab, pageSize) => {
                const status = currentTab === "published" ? "published" : "draft"
                dispatch(getQuestionnaireTemplates(INITIAL_PAGE, pageSize, status, term))
            }, DEBOUNCE_DELAY),
        [dispatch]
    )

    // Handle search with debounce
    useEffect(() => {
        debouncedSearch(searchText, tab, pagination.pageSize)
        return () => debouncedSearch.cancel()
    }, [searchText, tab, pagination.pageSize, debouncedSearch])

    // Handle page change
    const handlePageChange = (page) => {
        const status = tab === "published" ? "published" : "draft"
        dispatch(getQuestionnaireTemplates(page, pagination.pageSize, status, searchText))
    }

    // Handle page size change
    const handlePageSizeChange = (newPageSize) => {
        const status = tab === "published" ? "published" : "draft"
        setPagination((prev) => ({
            ...prev,
            pageSize: newPageSize,
            current: INITIAL_PAGE
        }))
        dispatch(getQuestionnaireTemplates(INITIAL_PAGE, newPageSize, status, searchText))
    }

    // Handle tab change
    const handleTabChange = (key) => {
        setPagination((prev) => ({
            ...prev,
            current: INITIAL_PAGE
        }))
        const status = key === "published" ? "published" : "draft"
        dispatch(getQuestionnaireTemplates(INITIAL_PAGE, pagination.pageSize, status, searchText))
    }

    return {
        templates,
        loading,
        metadata,
        pagination,
        handlePageChange,
        handlePageSizeChange,
        handleTabChange
    }
}
